import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import WithDefaults from "@mirage/storage/with-defaults";

import {
  createAvailableModelsService,
  type AvailableModelsStorage,
} from "@mirage/service-conversation/service/availableModelsService";
import {
  createConversationService,
  type ConversationStorage,
} from "@mirage/service-conversation/service/conversationService";
import {
  createWorkflowAgentsService,
  type WorkflowAgentsStorage,
} from "@mirage/service-conversation/service/workflowAgentsService";
import type { GuaranteedKVStorage, KVStorage } from "@mirage/storage";

export type Service = ReturnType<typeof conversationService>;

export enum StorageKey {
  Conversation = "conversation",
  WorkflowAgents = "workflow-agents",
}

export interface ConversationServiceStorage
  extends ConversationStorage,
    WorkflowAgentsStorage,
    AvailableModelsStorage {}

export default function conversationService(
  rawStorage: KVStorage<ConversationServiceStorage>,
) {
  const storage = setupConversationStorage(rawStorage);
  const conversationServiceImpl = createConversationService(storage);
  const workflowAgentsServiceImpl = createWorkflowAgentsService(storage);
  const availableModelsServiceImpl = createAvailableModelsService(storage);

  return services.provide(
    ServiceId.CONVERSATION,
    {
      ...conversationServiceImpl,
      ...workflowAgentsServiceImpl,
      ...availableModelsServiceImpl,
    },
    [ServiceId.DBX_API],
  );
}

/**
 * Setup the conversation storage with a default schema + migrations.
 */
export function setupConversationStorage(
  rawStorage: KVStorage<ConversationServiceStorage>,
): GuaranteedKVStorage<ConversationServiceStorage> {
  const storage = new WithDefaults(rawStorage, {
    conversations: {
      rows: {},
      version: 1,
    },
    conversationsLogging: {
      isEnabled: false,
      rows: [],
      version: 1,
    },
    conversationsDiagnostics: {
      isEnabled: false,
    },
    agents: {},
    availableModels: { models: [] },
    sources: {},
  });

  // add a migration to the queue to ensure the storage is always in the correct schema before all operations
  storage.queue.add(async () => {
    const conversations = await storage.storage.get("conversations");
    if (conversations?.version === undefined) {
      // old version with recongized schema, reset to empty without migrating
      await storage.storage.set("conversations", {
        rows: {},
        version: 1,
      });
    }
  });
  return storage;
}
